import { useMutation } from '@apollo/client';
import { FC, useState } from 'react';

import { cacheUtils } from '@eluve/apollo-client';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  HStack,
  Icon,
  NewButton,
  Paragraph,
  useToast,
} from '@eluve/components';
import {
  useAssignedTenantIdFromParams,
  useIsEluveAdmin,
} from '@eluve/session-helpers';
import { useDialog } from '@eluve/utility-hooks';

import {
  removeTenantUserMutation,
  updateTenantUserRoleMutation,
} from './operations';

type UserRowActionsProps = {
  userId: string;
  email: string;
  refetch: () => void;
  role: string;
};

export const UserRowActions: FC<UserRowActionsProps> = ({
  userId,
  email,
  refetch,
  role,
}) => {
  const { isDialogOpen, setIsDialogOpen } = useDialog(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const isEluveAdmin = useIsEluveAdmin();
  const isRoleMember = role === 'tenant-member';
  const tenantId = useAssignedTenantIdFromParams();

  const [removeUser] = useMutation(removeTenantUserMutation);
  const [updateUserRole] = useMutation(updateTenantUserRoleMutation);

  const toast = useToast();

  const handleOnActionClick = (isRemoving = false) => {
    setIsRemoving(isRemoving);
    setIsDialogOpen(true);
  };

  const handleOnRemoveClick = () => {
    setIsDialogOpen(false);
    removeUser({
      variables: { userId, tenantId },
      optimisticResponse: {
        deleteTenantUsersByPk: {
          __typename: 'TenantUsers',
          tenantId,
          userId,
        },
      },
      onError: () => {
        toast.error('User could not be removed from the organization.', {
          description: `Email: ${email}`,
        });
      },
      update: (cache) => {
        cacheUtils.evict({
          typeName: 'TenantUsers',
          key: { tenantId, userId },
        });
        cache.gc();
      },
    });
  };

  const handleOnRoleUpdateClick = () => {
    setIsDialogOpen(false);

    const updatedRole = isRoleMember ? 'tenant-admin' : 'tenant-member';

    updateUserRole({
      variables: {
        tenantId,
        userId,
        role: updatedRole,
      },
      optimisticResponse: {
        updateTenantUsersByPk: {
          __typename: 'TenantUsers',
          tenantId,
          userId,
          role: updatedRole,
        },
      },
      onError: () => {
        toast.error("User's role could not be changed.", {
          description: `Email: ${email}`,
        });
      },
      onCompleted: () => {
        refetch();
      },
    });
  };

  return (
    <HStack justify="end">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <NewButton type="outline" icon={{ name: 'Ellipsis' }} />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          alignOffset={-4}
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          <DropdownMenuItem onClick={() => handleOnActionClick()}>
            <Icon
              name={isRoleMember ? 'ChevronsUp' : 'ChevronsDown'}
              size="sm"
            />
            <span>{isRoleMember ? 'Change to admin' : 'Change to member'}</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={!isEluveAdmin}
            onClick={() => handleOnActionClick(true)}
          >
            <Icon name="X" size="sm" />
            <span>Remove user</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={isDialogOpen}>
        <DialogContent close={false}>
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
          </DialogHeader>
          <Paragraph color="secondary" size="md">
            {isRemoving
              ? `This will remove the user (${email}) from your organization.`
              : `This will change the role of the user (${email}) to "${isRoleMember ? 'Admin' : 'Member'}".`}
          </Paragraph>
          <DialogFooter>
            <NewButton
              onClick={() => {
                setIsDialogOpen(false);
              }}
              text="Cancel"
              type="outline"
            />
            <NewButton
              onClick={
                isRemoving ? handleOnRemoveClick : handleOnRoleUpdateClick
              }
              text={isRemoving ? 'Remove User' : 'Change Role'}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </HStack>
  );
};
